import React, { useEffect, useState } from "react";
import { fetchMediaImages } from "./adminMediaImageService";

const BASE_ROOT = process.env.REACT_APP_BASE_URL_ROOT || process.env.REACT_APP_BASE_URL?.replace(/\/api$/, "");
const IMAGE_USES = ["VOCABULARY", "GRAMMAR", "PROFILE", "GENERAL", "INTERNAL", "DRAFT"];
const PAGE_SIZE = 20;

const ImagePickerModal = ({ isOpen, onClose, onSelect, defaultImageUse = "VOCABULARY" }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [imageUseFilter, setImageUseFilter] = useState(defaultImageUse);
  const [visibilityFilter, setVisibilityFilter] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setPage(0);
  }, [isOpen, imageUseFilter, visibilityFilter]);

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setLoading(true);
      const res = await fetchMediaImages({
        page,
        size: PAGE_SIZE,
        visibility: visibilityFilter,
        imageUse: imageUseFilter,
      });
      if (res.status === 200) {
        const list = Array.isArray(res.data) ? res.data : res.data.content ?? [];
        setImages(list);
        const tp = Array.isArray(res.data)
          ? Math.ceil(list.length / PAGE_SIZE)
          : res.data.totalPages ?? 0;
        setTotalPages(tp);
      }
      setLoading(false);
    };
    load();
  }, [isOpen, page, imageUseFilter, visibilityFilter]);

  if (!isOpen) return null;

  const handleSelect = (img) => {
    const publicUrl = `/api/public/media-images/${img.id}/file`;
    onSelect(publicUrl, img.id);
    onClose();
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Pick an Image</span>
          <button style={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* Filters */}
        <div style={styles.filters}>
          <select value={imageUseFilter} onChange={(e) => { setImageUseFilter(e.target.value); setPage(0); }} style={styles.select}>
            <option value="">All Image Use</option>
            {IMAGE_USES.map((u) => <option key={u}>{u}</option>)}
          </select>
          <select value={visibilityFilter} onChange={(e) => { setVisibilityFilter(e.target.value); setPage(0); }} style={styles.select}>
            <option value="">All Visibility</option>
            <option>PUBLIC</option>
            <option>PRIVATE</option>
          </select>
        </div>

        {/* Grid */}
        <div style={styles.gridWrapper}>
          {loading ? (
            <p style={styles.msg}>Loading...</p>
          ) : images.length === 0 ? (
            <p style={styles.msg}>No images found. Upload images in the Media Images section.</p>
          ) : (
            <div style={styles.grid}>
              {images.map((img) => (
                <div key={img.id} style={styles.cell} onClick={() => handleSelect(img)}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.03)"; e.currentTarget.style.border = "2px solid #019095"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.border = "2px solid transparent"; }}
                >
                  <img src={`${BASE_ROOT}/api/public/media-images/${img.id}/file`} alt={img.name} style={styles.img} />
                  <p style={styles.imgName}>{img.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={styles.pagination}>
            <button style={styles.pageBtn} disabled={page === 0} onClick={() => setPage(page - 1)}>← Prev</button>
            <span style={{ fontSize: 13, color: "#555" }}>Page {page + 1} / {totalPages}</span>
            <button style={styles.pageBtn} disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  },
  modal: {
    background: "#fff", borderRadius: 12, padding: 24,
    width: "100%", maxWidth: 680, maxHeight: "85vh",
    overflowY: "auto", display: "flex", flexDirection: "column", gap: 16,
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  closeBtn: {
    background: "none", border: "none", fontSize: 22,
    cursor: "pointer", color: "#666", lineHeight: 1,
  },
  filters: { display: "flex", gap: 10 },
  select: { padding: "6px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13 },
  gridWrapper: { minHeight: 200, maxHeight: 400, overflowY: "auto" },
  grid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 },
  cell: {
    cursor: "pointer", borderRadius: 8, overflow: "hidden",
    border: "2px solid transparent", transition: "transform 0.15s, border 0.15s",
  },
  img: { width: "100%", height: 90, objectFit: "contain", background: "#f5f5f5", display: "block" },
  imgName: {
    fontSize: 11, color: "#555", margin: "4px 4px 4px",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  msg: { textAlign: "center", color: "#888", padding: "40px 0", fontSize: 13 },
  pagination: { display: "flex", alignItems: "center", justifyContent: "center", gap: 12 },
  pageBtn: {
    padding: "5px 14px", border: "1px solid #019095", background: "#fff",
    color: "#019095", borderRadius: 6, cursor: "pointer", fontSize: 13,
  },
};

export default ImagePickerModal;
